import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowRight, Users, UserCheck, FileText, Send, MessageSquare, Award, CheckCircle, Repeat } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FunnelStage } from '@/hooks/useMarketplaceAnalytics';

interface ActivationFunnelProps {
  title: string;
  description: string;
  stages: FunnelStage[];
  isLoading: boolean;
  variant: 'buyer' | 'vendor';
}

const buyerIcons = [Users, UserCheck, FileText, Send, MessageSquare, Award, CheckCircle, Repeat];
const vendorIcons = [Users, UserCheck, FileText, Send, Award, Repeat];

export function ActivationFunnel({ title, description, stages, isLoading, variant }: ActivationFunnelProps) {
  const icons = variant === 'buyer' ? buyerIcons : vendorIcons;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-32 w-28 flex-shrink-0" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-1 overflow-x-auto pb-2">
          {stages.map((stage, index) => {
            const Icon = icons[index] || Users;
            const isFirst = index === 0;
            const dropOff = !isFirst && stage.conversionRate < 50;
            const goodConversion = !isFirst && stage.conversionRate >= 70;

            return (
              <React.Fragment key={stage.name}>
                {index > 0 && (
                  <ArrowRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                )}
                <div
                  className={cn(
                    "flex flex-col items-center justify-center p-3 rounded-lg border min-w-[100px] flex-shrink-0",
                    dropOff && "border-amber-500/50 bg-amber-500/5",
                    goodConversion && "border-green-500/50 bg-green-500/5",
                    !dropOff && !goodConversion && "border-border bg-muted/30"
                  )}
                >
                  <Icon className={cn(
                    "h-5 w-5 mb-1",
                    dropOff && "text-amber-500",
                    goodConversion && "text-green-500",
                    !dropOff && !goodConversion && "text-muted-foreground"
                  )} />
                  <span className="text-2xl font-bold">{stage.count.toLocaleString()}</span>
                  <span className="text-xs text-muted-foreground text-center leading-tight">
                    {stage.name}
                  </span>
                  {!isFirst && (
                    <span className={cn(
                      "text-xs font-medium mt-1",
                      dropOff && "text-amber-500",
                      goodConversion && "text-green-500",
                      !dropOff && !goodConversion && "text-muted-foreground"
                    )}>
                      {stage.conversionRate}%
                    </span>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Summary stats */}
        <div className="mt-4 pt-4 border-t flex gap-6 text-sm">
          <div>
            <span className="text-muted-foreground">Overall Conversion:</span>{' '}
            <span className="font-medium">
              {stages.length > 1 && stages[0].count > 0
                ? Math.round((stages[stages.length - 1].count / stages[0].count) * 100)
                : 0}%
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Biggest Drop-off:</span>{' '}
            <span className="font-medium text-amber-500">
              {stages.reduce((worst, stage, i) => {
                if (i === 0) return worst;
                if (stage.conversionRate < worst.rate) {
                  return { name: stage.name, rate: stage.conversionRate };
                }
                return worst;
              }, { name: 'None', rate: 100 }).name}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
